import AddIcon from '@material-ui/icons/AddOutlined'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { useBlockUpdatesWhileFocused } from 'elements/hooks/useBlockUpdatesWhileFocused'
import Tooltip from 'elements/tooltip/Tooltip'
import { DateTimePicker, IconButton, MenuItem, Select, styled, TextField, TrashOutlineIcon, Typography } from 'opensolar-ui'
import { useMemo } from 'react'
import { doNotTranslate } from 'util/misc'
import wildcard from 'wildcard'
import { FilterCondition, FilterField, FilterStruct, Op } from '../../conditions/types'
import { createDefaultCondition, opToSymbol } from '../../conditions/utils/utils'

const nowish = Date.now()

const Wrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
}))
const RowsWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.grey[50],
  borderRadius: 4,
  padding: 16,
  gap: 8,
}))
const RowWrapper = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  gap: 16,
  alignItems: 'start',
  margin: 4,
})
const OpSelect = styled(Select)(({ theme }) => ({
  width: 140,
  [theme.breakpoints.down('md')]: {
    width: 100,
  },
}))
const Value = styled('div')({
  flexGrow: 1,
  flexBasis: '50px',
})
const Separator = styled('div')({
  margin: 16,
  backgroundColor: 'white',
  zIndex: 20,
  alignSelf: 'center',
})
const Actions = styled('div')({
  display: 'flex',
  height: 29,
  gap: 16,
})

type BaseProps = {
  fields: FilterField[]
  ops: Op[]
  fieldFreeSolo?: boolean
}

type FilterConditionBuilderProps = BaseProps & {
  filter: FilterStruct
  onChange: (filter: FilterStruct) => void
  onRemove: (filter: FilterStruct) => void
  isLast?: boolean
}

export const FilterConditionBuilder = ({
  filter,
  ops,
  onChange,
  onRemove,
  fields,
  isLast,
  fieldFreeSolo,
}: FilterConditionBuilderProps) => {
  const onConditionChange = (condition: FilterCondition) => {
    onChange(filter)
  }
  const onConditionRemove = (condition: FilterCondition) => {
    filter.conditions = filter.conditions.filter((c) => c !== condition)
    if (filter.conditions.length === 0) onRemove(filter)
    else onChange(filter)
  }
  const onAddConditionAfter = (condition: FilterCondition) => {
    const index = filter.conditions.indexOf(condition)
    filter.conditions.splice(index + 1, 0, createDefaultCondition(fields[0], ops[0]))
    onChange(filter)
  }
  return (
    <Wrapper>
      <RowsWrapper>
        {filter.conditions.map((condition, index) => (
          <ConditionBuilderRow
            key={index}
            condition={condition}
            ops={ops}
            onChange={onConditionChange}
            onRemove={onConditionRemove}
            onAddAfter={onAddConditionAfter}
            fields={fields}
            fieldFreeSolo={fieldFreeSolo}
          />
        ))}
      </RowsWrapper>
      {!isLast && <Separator>{doNotTranslate('OR')}</Separator>}
    </Wrapper>
  )
}

type ConditionBuilderRowProps = BaseProps & {
  condition: FilterCondition
  onChange: (condition: FilterCondition) => void
  onRemove: (condition: FilterCondition) => void
  onAddAfter: (condition: FilterCondition) => void
}

const ConditionBuilderRow = ({
  condition,
  ops,
  onChange,
  onRemove,
  onAddAfter,
  fields,
  fieldFreeSolo,
}: ConditionBuilderRowProps) => {
  const onFieldSelectChange = (e) => {
    condition.path = e.target.value
    onChange(condition)
  }
  const onFieldInputChange = (e, val) => {
    condition.path = val
    onChange(condition)
  }

  const { onChange: onInputChanged, forceInnerUpdate, ...fieldProps } = useBlockUpdatesWhileFocused({
    value: condition.path,
    onChange: onFieldInputChange,
    noInternalValueUpdates: true,
  })

  // onFieldAutocompleteChange gets fired after onFieldInputChange, so should correct the formatted value sent through there
  const onFieldAutocompleteChange = (_, val) => {
    if (!val?.path) return
    condition.path = val.path
    onChange(condition)
    forceInnerUpdate(condition.path)
  }
  const onOpChange = (e) => {
    condition.op = e.target.value as Op
    onChange(condition)
  }
  const onBooleanChange = (e) => {
    const val = e.target.value
    if (val === 'yes') condition.value = true
    else if (val === 'no') condition.value = false
    else condition.value = ''
    onChange(condition)
  }
  const onValueChange = (e) => {
    condition.value = parseStringValue(e.target.value)
    onChange(condition)
  }
  const onTimestampChange = (date) => {
    condition.value = date.getTime() / 1000
    onChange(condition)
  }
  const onArrayChange = (e) => {
    condition.value = e.target.value.split(',').map(parseStringValue)
    onChange(condition)
  }

  const currentField = useMemo(() => {
    const exactMatch = fields.find((field) => field.path === condition.path)
    if (exactMatch) return exactMatch
    return fields.find((field) => wildcard(field.path, condition.path))
  }, [fields, condition.path])

  const parseStringValue = (value: string) => {
    if (!value) return null
    if (value === 'true') return true
    if (value === 'false') return false

    // For string type fields, always return as string and never convert to number
    if (currentField?.type === 'string') {
      return value
    }

    const asFloat = parseFloat(value)
    if (!isNaN(asFloat) && asFloat + '' === value.trim()) return asFloat

    return value
  }

  const arrayMode = useMemo(() => condition.op === 'in' || condition.op === 'nin', [condition.op])

  const showOps = useMemo(() => {
    // 'boolean' types are incompatible with op selector
    const isBoolean = currentField?.type === 'boolean'
    if (isBoolean && condition.op !== 'eq') {
      condition.op = 'eq'
      onChange(condition)
    }

    return !isBoolean && currentField?.type !== 'semver'
  }, [currentField])

  let onTypedValueChange: ((value: any) => void) | undefined = undefined
  var value: any
  if (currentField?.type === 'boolean') {
    onTypedValueChange = onBooleanChange
    if (condition.value === true) value = 'yes'
    else if (condition.value === false) value = 'no'
    else value = 'not_defined'
  } else if (arrayMode) {
    onTypedValueChange = onArrayChange
    value = valueAsString(condition.value)
  } else if (currentField?.type === 'timestamp') {
    onTypedValueChange = onTimestampChange
    value = condition.value * 1000
  } else {
    onTypedValueChange = onValueChange
    value = valueAsString(condition.value)
  }

  const nonBlockedInput = {
    value: value,
    onChange: onTypedValueChange,
  }

  // This means that the value is not updated until the user clicks out of the input
  // This improves performance by not changing/re-rendering the entire form while the user is typing
  const blockedInput = useBlockUpdatesWhileFocused(nonBlockedInput)

  // Warning, as ConditionBuilderRow gets keyed by index, all of this componentry needs to react well to data
  // changes coming through from the condition object
  return (
    <RowWrapper>
      <Value>
        {fieldFreeSolo ? (
          <Tooltip title={currentField && doNotTranslate(currentField?.label)} placement="right">
            <Autocomplete
              options={fields}
              groupBy={(option) => option.group || doNotTranslate('Other')}
              getOptionLabel={(option) =>
                !option ? '' : typeof option === 'string' ? option : `${option.label} ${option.path}`
              }
              renderOption={(option) => (
                <div>
                  <Typography textVariant="body1">{option.label}</Typography>
                  <Typography textVariant="caption2">{option.path}</Typography>
                </div>
              )}
              onChange={onFieldAutocompleteChange}
              onInputChange={onInputChanged}
              renderInput={(params) => (
                <TextField
                  {...params}
                  margin="none"
                  variant="standard"
                  helperText={currentField?.help ? doNotTranslate(currentField.help) : undefined}
                />
              )}
              freeSolo={true}
              {...fieldProps}
            />
          </Tooltip>
        ) : (
          <Select
            margin="none"
            variant="standard"
            value={condition.path}
            onChange={onFieldSelectChange}
            fullWidth={true}
          >
            {fields.map((field) => (
              <MenuItem key={field.path} value={field.path}>
                {doNotTranslate(field.label)}
              </MenuItem>
            ))}
          </Select>
        )}
      </Value>

      <OpSelect
        margin="none"
        variant="standard"
        value={condition.op}
        onChange={onOpChange}
        fullWidth={true}
        style={{ opacity: showOps ? 1 : 0, pointerEvents: showOps ? 'auto' : 'none' }}
      >
        {ops.map((op) => (
          <MenuItem key={op} value={op}>
            {doNotTranslate(opToSymbol(op))}
          </MenuItem>
        ))}
      </OpSelect>

      <Value>
        {/* Not found */}
        {!currentField && !fieldFreeSolo && doNotTranslate('Field not found')}

        {/* Boolean */}
        {currentField?.type === 'boolean' ? (
          <Select margin="none" variant="standard" fullWidth={true} {...nonBlockedInput}>
            <MenuItem value="yes">{doNotTranslate('Yes')}</MenuItem>
            <MenuItem value="no">{doNotTranslate('No')}</MenuItem>
            <MenuItem value="not_defined">{doNotTranslate('Not Defined')}</MenuItem>
          </Select>
        ) : arrayMode ? (
          //TODO: replace with non-RA ChipsInput
          <TextField margin="none" variant="standard" fullWidth={true} {...blockedInput} />
        ) : currentField?.type === 'timestamp' ? (
          <DateTimePicker
            margin="none"
            inputVariant="standard"
            fullWidth={true}
            {...blockedInput}
            defaultValue={nowish}
            emptyLabel={doNotTranslate('Select date')}
          />
        ) : (
          <TextField margin="none" variant="standard" fullWidth={true} {...blockedInput} />
        )}
      </Value>

      <Actions>
        <Tooltip title={doNotTranslate('Add AND Condition After')}>
          <IconButton size="small" onClick={() => onAddAfter(condition)}>
            <AddIcon />
          </IconButton>
        </Tooltip>

        <IconButton size="small" onClick={() => onRemove(condition)}>
          <TrashOutlineIcon />
        </IconButton>
      </Actions>
    </RowWrapper>
  )
}

function valueAsArray(value: any) {
  return Array.isArray(value) ? value : typeof value === 'string' ? value.split(',') : [value]
}

function valueAsString(value: any) {
  return typeof value === 'string' ? value : Array.isArray(value) ? value.join(',') : value + ''
}
